/**
 * Minimal, dependency-free ZIP writer — the inverse of unzip.ts. Emits a
 * STORED (uncompressed, method 0) archive, which every unzipper (incl. our
 * own reader) accepts. `.arch` workspaces are small text, so skipping
 * DEFLATE costs nothing and keeps this self-contained.
 *
 * Produces a standard local-header + central-directory + EOCD layout with
 * real CRC-32s, so the output round-trips through unzip() and OS tooling.
 */

const SIG_LFH = 0x04034b50; // local file header
const SIG_CDH = 0x02014b50; // central directory file header
const SIG_EOCD = 0x06054b50; // end of central directory

// Standard CRC-32 (IEEE 802.3), table built once on first use.
let crcTable: Uint32Array | null = null;
function crc32(bytes: Uint8Array): number {
  if (!crcTable) {
    crcTable = new Uint32Array(256);
    for (let n = 0; n < 256; n++) {
      let c = n;
      for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
      crcTable[n] = c >>> 0;
    }
  }
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) {
    crc = (crc >>> 8) ^ crcTable[(crc ^ bytes[i]) & 0xff];
  }
  return (crc ^ 0xffffffff) >>> 0;
}

interface Entry {
  readonly nameBytes: Uint8Array;
  readonly data: Uint8Array;
  readonly crc: number;
  readonly offset: number;
}

/** Build a STORED `.zip` archive from a virtual file map. */
export function zipStore(files: ReadonlyMap<string, string>): Uint8Array {
  const enc = new TextEncoder();
  const local: Uint8Array[] = [];
  const entries: Entry[] = [];
  let offset = 0;

  for (const [path, contents] of files) {
    const name = path.replace(/^\/+/, ""); // zip paths are relative
    const nameBytes = enc.encode(name);
    const data = enc.encode(contents);
    const crc = crc32(data);

    const header = new Uint8Array(30 + nameBytes.length);
    const hv = new DataView(header.buffer);
    hv.setUint32(0, SIG_LFH, true);
    hv.setUint16(4, 20, true); // version needed
    hv.setUint16(6, 0, true); // flags
    hv.setUint16(8, 0, true); // method = STORED
    hv.setUint16(10, 0, true); // mod time
    hv.setUint16(12, 0x21, true); // mod date (1980-01-01)
    hv.setUint32(14, crc, true);
    hv.setUint32(18, data.length, true); // compressed size
    hv.setUint32(22, data.length, true); // uncompressed size
    hv.setUint16(26, nameBytes.length, true);
    hv.setUint16(28, 0, true); // extra len
    header.set(nameBytes, 30);

    entries.push({ nameBytes, data, crc, offset });
    local.push(header, data);
    offset += header.length + data.length;
  }

  const central: Uint8Array[] = [];
  let cdSize = 0;
  for (const e of entries) {
    const rec = new Uint8Array(46 + e.nameBytes.length);
    const rv = new DataView(rec.buffer);
    rv.setUint32(0, SIG_CDH, true);
    rv.setUint16(4, 20, true); // version made by
    rv.setUint16(6, 20, true); // version needed
    rv.setUint16(8, 0, true); // flags
    rv.setUint16(10, 0, true); // method = STORED
    rv.setUint16(12, 0, true); // mod time
    rv.setUint16(14, 0x21, true); // mod date
    rv.setUint32(16, e.crc, true);
    rv.setUint32(20, e.data.length, true); // compressed size
    rv.setUint32(24, e.data.length, true); // uncompressed size
    rv.setUint16(28, e.nameBytes.length, true);
    rv.setUint16(30, 0, true); // extra len
    rv.setUint16(32, 0, true); // comment len
    rv.setUint16(34, 0, true); // disk number
    rv.setUint16(36, 0, true); // internal attrs
    rv.setUint32(38, 0, true); // external attrs
    rv.setUint32(42, e.offset, true); // local header offset
    rec.set(e.nameBytes, 46);
    central.push(rec);
    cdSize += rec.length;
  }

  const eocd = new Uint8Array(22);
  const ev = new DataView(eocd.buffer);
  ev.setUint32(0, SIG_EOCD, true);
  ev.setUint16(4, 0, true); // disk number
  ev.setUint16(6, 0, true); // cd start disk
  ev.setUint16(8, entries.length, true);
  ev.setUint16(10, entries.length, true);
  ev.setUint32(12, cdSize, true);
  ev.setUint32(16, offset, true); // cd offset
  ev.setUint16(20, 0, true); // comment len

  const total =
    offset + cdSize + eocd.length;
  const out = new Uint8Array(total);
  let pos = 0;
  for (const chunk of local) {
    out.set(chunk, pos);
    pos += chunk.length;
  }
  for (const chunk of central) {
    out.set(chunk, pos);
    pos += chunk.length;
  }
  out.set(eocd, pos);
  return out;
}
